import type { KeyFinding, KeyFindingType } from "#/types/report";
import {
  IconAlertTriangle,
  IconCircleCheck,
  IconFlame,
  IconInfoCircle,
} from "@tabler/icons-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import ReportSection from "./ReportSection";

const FINDING_STYLES: Record<
  KeyFindingType,
  { bgClass: string; borderClass: string; textClass: string; icon: typeof IconFlame }
> = {
  critical: {
    bgClass: "bg-red-50",
    borderClass: "border-red-200",
    textClass: "text-red-700",
    icon: IconFlame,
  },
  warning: {
    bgClass: "bg-amber-50",
    borderClass: "border-amber-200",
    textClass: "text-amber-700",
    icon: IconAlertTriangle,
  },
  info: {
    bgClass: "bg-blue-50",
    borderClass: "border-blue-200",
    textClass: "text-blue-700",
    icon: IconInfoCircle,
  },
  success: {
    bgClass: "bg-green-50",
    borderClass: "border-green-200",
    textClass: "text-green-700",
    icon: IconCircleCheck,
  },
};

interface KeyFindingsProps {
  findings: KeyFinding[];
  tooltip?: ReactNode;
}

const KeyFindings = ({ findings, tooltip }: KeyFindingsProps) => {
  const { t } = useTranslation();

  if (findings.length === 0) return null;

  return (
    <ReportSection title={t("reports.inventoryScan.keyFindings.title")} tooltip={tooltip}>
      <div className="flex flex-col gap-2">
        {findings.map((finding, index) => {
          const style = FINDING_STYLES[finding.type];
          const Icon = style.icon;

          return (
            <div
              key={index}
              className={`flex items-start gap-3 rounded-lg border px-4 py-3 ${style.bgClass} ${style.borderClass}`}
            >
              <Icon size={18} className={`mt-0.5 shrink-0 ${style.textClass}`} />
              <span className={`text-sm ${style.textClass}`}>{finding.message}</span>
            </div>
          );
        })}
      </div>
    </ReportSection>
  );
};

export default KeyFindings;
